import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";

import Card from "@/blueprint/components/Card/Card";
import Eyebrow from "@/blueprint/components/Eyebrow/Eyebrow";
import HotspotImage from "@/blueprint/components/HotspotImage/HotspotImage";
import Text from "@/blueprint/components/Text/Text";
import { useAnnounce } from "@/blueprint/components/LiveAnnouncer/useAnnounce";
import { MotionDuration, MotionEasing } from "@/blueprint/tokens/Motion";
import { MADE_IN_USA, WHATS_INSIDE } from "@/views/shop/shopContent";
import Section from "@/blueprint/components/Section/Section";
import { useTrack } from "@/analytics/events";

// Figma's "Graphic" box (node 10583-55465) is 924x673 with the flat-lay placed
// at (-269, -165) rendering 1392x892.785 -- but NOTHING in the design clips.
// Graphic, its parent Main Graphic, and the Product Carousel (10583-55462) are
// all nominally 673 tall and all render at the image's full 1392x893: the
// designer sees the whole flat-lay, including ~165px of margin above the dock
// and the drop shadow below the cleaning head. The 924x673 box is a layout
// reference, not a crop.
//
// So we show the image's full height and keep Figma's horizontal placement.
// The box is 924 wide x 892.785 tall; the only thing trimmed is 269px of empty
// margin at the left and 199px at the right, neither of which holds a product
// (objects span image x 372..1091). Cropping the height instead -- 673, or the
// 626 an earlier pass used -- cuts the dock off at the top and clips the
// shadow at the bottom.
const FLATLAY_IMAGE_STYLE = {
  left: "-29.1126%", // -269 / 924
  top: "0%",
  width: "150.6494%", // 1392 / 924
  height: "100%", // the box is the image's full 892.785 height
};

type WhatsInsideItem = (typeof WHATS_INSIDE.items)[number];

type ItemDetailsProps = {
  item: WhatsInsideItem;
  headingId?: string;
  layout?: "panel" | "card";
  cardIndex?: number;
};

const ItemDetails = ({
  item,
  headingId,
  layout = "panel",
  cardIndex = 0,
}: ItemDetailsProps) => {
  const detailHeading =
    "detailHeading" in item ? item.detailHeading : undefined;
  const isCard = layout === "card";

  return (
    <div className="flex flex-col gap-xl">
      {isCard ? (
        // Figma shows Matic (first) in orange and Dock (second) in olive;
        // alternate those sampled colors for the remaining carousel cards.
        <span
          className={`inline-flex w-fit rounded-8 px-s py-xs font-trimMono text-[14px] font-normal leading-[24px] text-content-inverse ${
            cardIndex % 2 === 0 ? "bg-brand-orange" : "bg-brand-olive"
          }`}
        >
          {item.count}
        </span>
      ) : (
        <Eyebrow
          variant="eyebrow-sm"
          tone="inverse"
          className="inline-flex w-fit rounded-8 bg-brand-orange px-s py-xs"
        >
          {item.count}
        </Eyebrow>
      )}

      <div className="flex flex-col gap-xs">
        {isCard ? (
          <Text variant="wib-card-name" as="h3">
            <span id={headingId}>{item.name}</span>
          </Text>
        ) : (
          <Text variant="h5" as="h3">
            <span id={headingId}>{item.name}</span>
          </Text>
        )}
        {isCard ? (
          <Text variant="wib-card-body">{item.description}</Text>
        ) : (
          <Text variant="paragraph-md">{item.description}</Text>
        )}
      </div>

      {item.details.length > 0 && (
        <>
          <div
            className={
              isCard
                ? "border-t border-neutral1"
                : "border-t border-tint-black-20"
            }
          />
          <div className="flex flex-col gap-m">
            {detailHeading !== undefined &&
              (isCard ? (
                <Text variant="wib-card-body">{detailHeading}</Text>
              ) : (
                <Text variant="paragraph-md">{detailHeading}</Text>
              ))}
            {item.details.map((detail) => (
              <div
                key={"label" in detail ? detail.label : detail.value}
                className="flex flex-col gap-1"
              >
                {"label" in detail &&
                  (isCard ? (
                    <Text variant="wib-card-body">{detail.label}</Text>
                  ) : (
                    <Text variant="paragraph-md">{detail.label}</Text>
                  ))}
                {isCard ? (
                  <Text variant="wib-card-body" tone="secondary">
                    {detail.value}
                  </Text>
                ) : (
                  <Text variant="paragraph-md">{detail.value}</Text>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const DesktopWhatsInside = () => {
  const announce = useAnnounce();
  const track = useTrack();
  const [selectedId, setSelectedId] = useState<string>(
    WHATS_INSIDE.items[0].id,
  );
  const reducedMotion = useReducedMotion() === true;
  const activeItem =
    WHATS_INSIDE.items.find((item) => item.id === selectedId) ??
    WHATS_INSIDE.items[0];
  const fadeDuration = reducedMotion ? 0 : MotionDuration.quickMs / 1000;
  /* Items differ in detail count, so the card is a different height per item.
     The shell holds still and its height glides between contents; measured
     here because CSS cannot transition to auto. */
  const detailsRef = useRef<HTMLDivElement>(null);
  const [detailsHeight, setDetailsHeight] = useState<number | null>(null);

  useEffect(() => {
    const element = detailsRef.current;
    if (element === null) {
      return;
    }
    const observer = new ResizeObserver(() =>
      setDetailsHeight(element.offsetHeight),
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const selectItem = (id: string) => {
    const item = WHATS_INSIDE.items.find((candidate) => candidate.id === id);
    if (item === undefined) return;

    setSelectedId(id);
    // Desktop only: the mobile carousel is native scroll with no selection.
    track("hotspot_selected", { item_id: item.id });
    announce(item.name);
  };

  return (
    // items-center, because the box is not the picture. Now that the flat-lay
    // renders its full height it carries the photo's own empty margin — 19% of
    // the height above the dock and 15.5% below the cleaning head — so the
    // products occupy the middle 65% and their centre sits at 51.7%. Top
    // aligning puts the card's whole upper third opposite blank image; centring
    // on the box lands within 14px of centring on the products themselves.
    <div className="mt-full hidden w-full max-w-[1330px] gap-l sd:grid sd:grid-cols-[minmax(320px,382px)_minmax(0,924px)] sd:items-center">
      <div
        id="whats-inside-details"
        role="region"
        aria-labelledby="whats-inside-detail-heading"
        className="min-w-0"
      >
        {/* The card shell never leaves — swapping the whole bordered card
            blinked it out of existence and snapped the layout to the next
            item's height. Only the content crossfades, stepping in the
            direction of travel, while the shell's height glides. */}
        <Card className="border border-tint-black-20 bg-brand-white p-card-lg">
          <motion.div
            initial={false}
            {...(detailsHeight === null
              ? {}
              : { animate: { height: detailsHeight } })}
            transition={{
              duration: reducedMotion ? 0 : MotionDuration.mediumMs / 1000,
              ease: [...MotionEasing.inOut],
            }}
            className="overflow-hidden"
          >
            <div ref={detailsRef}>
              <AnimatePresence initial={false} mode="wait">
                <motion.div
                  key={activeItem.id}
                  initial={{
                    opacity: reducedMotion ? 1 : 0,
                    y: reducedMotion ? 0 : 12,
                  }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{
                    opacity: reducedMotion ? 1 : 0,
                    y: reducedMotion ? 0 : -12,
                  }}
                  transition={{
                    duration: fadeDuration,
                    ease: [...MotionEasing.inOut],
                  }}
                >
                  <ItemDetails
                    item={activeItem}
                    headingId="whats-inside-detail-heading"
                  />
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>
        </Card>
      </div>

      <HotspotImage
        image={WHATS_INSIDE.image}
        alt={WHATS_INSIDE.imageAlt}
        hotspots={WHATS_INSIDE.hotspots}
        selectedId={activeItem.id}
        onHotspotClick={selectItem}
        imageStyle={FLATLAY_IMAGE_STYLE}
        imageSizes="(min-width: 1600px) 1410px, 153vw"
        hotspotClassName="h-[43px] w-[43px] [&>svg]:h-5 [&>svg]:w-5"
        // mask-edge-fade: the flat-lay's ambient grey is 1-3 levels darker than
        // the page, so its straight edge reads as the shadow being cut off. The
        // fade is 32px and every marker sits at least 88px from an edge, so
        // nothing interactive lands in it.
        className="w-full overflow-hidden rounded-16 mask-edge-fade [aspect-ratio:924/892.785]"
      />
    </div>
  );
};

const WhatsInside = ({ position }: { position: number }) => (
  <Section
    id="whats-inside"
    position={position}
    ariaLabelledBy="whats-inside-heading"
    className="flex flex-col items-center overflow-x-clip px-grid-margin py-page"
  >
    <div className="flex flex-col items-center gap-quarter text-center">
      <Eyebrow variant="eyebrow-lg">{WHATS_INSIDE.eyebrow}</Eyebrow>
      <Text variant="h3" as="h2" className="tab:text-h2 sd:text-h3">
        <span id="whats-inside-heading">{WHATS_INSIDE.title}</span>
      </Text>
    </div>

    <DesktopWhatsInside />

    {/* Card width leaves 28px = 12px gap + 16px next-card peek (5642-5668). */}
    <div className="hideScrollBar mt-full flex w-full snap-x snap-mandatory gap-third overflow-x-auto overflow-y-clip sd:hidden">
      {WHATS_INSIDE.items.map((item, index) => (
        <div
          key={item.id}
          className="w-[calc(100%-28px)] max-w-[338px] shrink-0 snap-start tab:w-[475px] tab:max-w-none"
        >
          <Card
            radius="l"
            className="flex h-full flex-col border border-tint-black-20 bg-brand-white"
          >
            <div className="relative w-full shrink-0 bg-brand-grey [aspect-ratio:338/216] tab:[aspect-ratio:475/304]">
              <Image
                src={item.image}
                alt=""
                fill
                sizes="(min-width: 1280px) 0px, (min-width: 800px) 475px, 338px"
                className="object-cover"
              />
            </div>
            <div className="flex-1 p-card-lg">
              <ItemDetails item={item} layout="card" cardIndex={index} />
            </div>
          </Card>
        </div>
      ))}
    </div>

    <div className="mt-double flex w-full max-w-[1330px] flex-col items-center justify-center gap-[48px] border-t border-black/10 py-[48px] tab:flex-row">
      <Image
        src={MADE_IN_USA.image}
        alt={MADE_IN_USA.imageAlt}
        width={173}
        height={90}
        sizes="173px"
        className="h-[90px] w-[173px] shrink-0 object-contain"
      />
      <div className="flex w-full max-w-[797px] flex-col gap-s">
        <Text variant="h6" as="h3">
          {MADE_IN_USA.title}
        </Text>
        <Text variant="paragraph-md" tone="secondary">
          {MADE_IN_USA.body}
        </Text>
      </div>
    </div>
  </Section>
);

export default WhatsInside;
